import { ConfigServiceModel } from '@app/origami-api/services/config.service';
import { TopicTypeNameById } from '@app/origami-api/constants/topic-type-names';
import { Locale } from '@app/origami-api/constants/default-locales';
import { FormFieldConfig, SUPPORT_VALIDATION_FIELD_TYPES } from '@app/shared/forms/dynamic-form/fields.service';
import { ValidatorFn, Validators } from '@angular/forms';
import { Field } from '@app/origami-api/constants/field-flag';
import { ValidationRegex } from '@app/origami-api/constants/validation-regex';
import { getSpecialValidatorFn } from '@app/shared/utils/special-validators';
import { FieldType } from '@app/origami-api/constants/field-type';
import { FieldNames } from '@app/origami-api/constants/field-names';
import { FieldLength } from '@app/origami-api/constants/fields-length';
import { isEqual, isFunction, some } from 'lodash';
import {
  LayoutField,
  LayoutGroup,
} from '@app/pages/main/pages/quality/pages/quality-settings/pages/form-setting/components/form-layout-editor-modal/components/form-layout-editor/models';
import { TimeZoneService } from '@app/services/time-zone.service';

export enum CustomProperties {
  patched = '__patched',
  validators = '__validators',
  defaultState = '__defaultState',
  setValuesKeys = '__setValuesKeys',
  originalValue = '__originalValue',
  autoValue = '__autoValue',
}

export function localizeFieldsTitles(form: ConfigServiceModel.Form): ConfigServiceModel.Form {
  if (!form) {
    return form;
  }

  const currentLocale = localStorage.getItem('ti-loc') as Locale; // ugly solution but we can't use session service here

  if (currentLocale === Locale.en_US) {
    return form;
    // nothing to localize
  }

  (form.Fields || form.FormFields || []).forEach((ff) => {
    const topicTypeName = TopicTypeNameById?.[form.TopicTypeId];
    if (!topicTypeName) {
      return;
    }

    const localized = tiStrings.shared.defaultFieldTitles?.[topicTypeName]?.[ff.FieldName];
    if (!localized) {
      return;
    }

    // DB may store NULL when the title matches the server default; resolve for display in non-EN locales.
    if (ff.Title == null || ff.Title === ff.DefaultTitle) {
      ff.Title = localized;
    }
  });

  return form;
}

export function getValidatorFn(fieldConfig: FormFieldConfig, timeZoneService: TimeZoneService): ValidatorFn[] {
  const validators = [];
  if (Field.Is.required(fieldConfig.flags)) {
    validators.push(Validators.required);
  }
  if (Field.Is.requiredEmail(fieldConfig.flags)) {
    //validators.push(Validators.email);
    validators.push(Validators.pattern(ValidationRegex.email));
  }
  if (fieldConfig.UIData?.pattern) {
    validators.push(Validators.pattern(fieldConfig.UIData.pattern));
  }

  if (
    fieldConfig.UIData &&
    fieldConfig.UIData.validation &&
    fieldConfig.UIData.validation.type &&
    SUPPORT_VALIDATION_FIELD_TYPES.has(fieldConfig.fieldType)
  ) {
    const validator = getSpecialValidatorFn({
      type: fieldConfig.UIData.validation.type,
      validationOptions: fieldConfig.UIData.validation.options,
      fieldConfig: fieldConfig,
      timeZoneService: timeZoneService,
    });
    validators.push(validator);
  }

  if (fieldConfig.fieldType === FieldType.LongText) {
    //todo: may be we should have that maxFieldLength for all LongText-fields
    const maxFieldLength =
      fieldConfig.UIData?.maxFieldLength || fieldConfig.fieldName === FieldNames.eSignature.Comments
        ? FieldLength.MaxFieldLength.topicAttributeMaxStringLength
        : Math.floor(FieldLength.WCF.maxRequestLength / 10); //TRL-1393
    validators.push(Validators.maxLength(maxFieldLength));
  }

  if (fieldConfig.fieldType === FieldType.Text) {
    const maxFieldLength =
      fieldConfig.UIData?.maxFieldLength || FieldLength.MaxFieldLength.topicAttributeMaxStringLength;
    validators.push(Validators.maxLength(maxFieldLength));
  }

  const maxDate = fieldConfig.UIData?.maxDate;
  if (maxDate instanceof Date) {
    const maxDateValidator: ValidatorFn = (control) => {
      const value = control.value;
      return value instanceof Date && value > maxDate ? { maxError: { maxValue: maxDate } } : null;
    };
    validators.push(maxDateValidator);
  }

  const minDate = fieldConfig.UIData?.minDate;
  if (minDate instanceof Date) {
    const minDateValidator: ValidatorFn = (control) => {
      const value = control.value;
      return value instanceof Date && value < minDate ? { minError: { minValue: minDate } } : null;
    };
    validators.push(minDateValidator);
  }

  const minDateFromField = fieldConfig.UIData?.minDateFromField;
  if (minDateFromField) {
    const dateBeforeMinFieldValidator: ValidatorFn = (control) => {
      const value = control.value;
      const siblingValue = control.parent?.get(minDateFromField)?.value;
      return value instanceof Date && siblingValue instanceof Date && value < siblingValue
        ? { dateBeforeMinField: true }
        : null;
    };
    validators.push(dateBeforeMinFieldValidator);
  }

  return validators.filter(isFunction);
}

export function hasRenderableFields(
  collectionMap: Record<string, (LayoutField | LayoutGroup)[]>,
  sectionName: string,
  fieldNames: Set<string>,
): boolean {
  const visited = new Set<string>();

  // A section counts as non-empty only if a renderable field sits somewhere below it: group
  // templates are guarded by `@if`, so a section holding only empty groups renders blank.
  function traverse(collectionName: string): boolean {
    if (!collectionName || visited.has(collectionName)) {
      return false;
    }

    visited.add(collectionName);

    return some(collectionMap?.[collectionName] ?? [], (item) =>
      item.type === 'field' ? fieldNames.has(item.name) : traverse(item.name),
    );
  }

  return traverse(sectionName);
}

// User toggles survive a rebuild only while the entity and the config's section defaults stay the same.
export function resolveSectionState(
  current: Record<string, boolean>,
  defaults: Record<string, boolean>,
  prevDefaults: Record<string, boolean> | undefined,
  entityChanged: boolean,
): Record<string, boolean> {
  return entityChanged || !isEqual(defaults, prevDefaults) ? defaults : current;
}
